export const AUTO_DOWNLOADED_OPERATION_IDS_KEY =
    "kaamfu.auto_downloaded_operation_ids";

export const DISMISSED_OPERATION_IDS_KEY = "kaamfu.dismissed_operation_ids";

export const COMPLETED_OPERATION_RETENTION_MS = 60 * 60 * 1000;

const MAX_STORED_IDS = 100;

export function operationIdKey(id) {
    if (id == null || id === "") {
        return null;
    }

    return String(id);
}

export function operationIdsMatch(left, right) {
    const key = operationIdKey(left);

    return key !== null && key === operationIdKey(right);
}

export function hasAutoDownloadedOperationId(ids, id) {
    const key = operationIdKey(id);

    if (!key) {
        return false;
    }

    return (ids ?? []).some((stored) => operationIdKey(stored) === key);
}

/**
 * @param {Array<string|number>} autoDownloadedIds
 * @param {string|number} id
 * @param {{ manual?: boolean }} [options]
 */
export function shouldDownloadExport(
    autoDownloadedIds,
    id,
    { manual = false } = {},
) {
    if (manual) {
        return true;
    }

    return !hasAutoDownloadedOperationId(autoDownloadedIds, id);
}

export function hasDismissedOperationId(ids, id) {
    const key = operationIdKey(id);

    if (!key) {
        return false;
    }

    return (ids ?? []).some((stored) => operationIdKey(stored) === key);
}

/**
 * @param {string|undefined|null} updatedAt
 * @param {number} [now]
 */
export function isWithinCompletedOperationRetention(updatedAt, now = Date.now()) {
    if (!updatedAt) {
        return true;
    }

    const updatedTime = new Date(updatedAt).getTime();

    if (Number.isNaN(updatedTime)) {
        return true;
    }

    return now - updatedTime < COMPLETED_OPERATION_RETENTION_MS;
}

/**
 * @param {{ id?: string|number, updated_at?: string|null, created_at?: string|null }} item
 * @param {{ dismissedIds?: Array<string|number>, now?: number }} [options]
 */
export function isCompletedOperationVisible(
    item,
    { dismissedIds = [], now = Date.now() } = {},
) {
    if (!item?.id) {
        return false;
    }

    if (hasDismissedOperationId(dismissedIds, item.id)) {
        return false;
    }

    const updatedAt = item.updated_at ?? item.created_at ?? null;

    return isWithinCompletedOperationRetention(updatedAt, now);
}

export function loadAutoDownloadedOperationIds(storage = getSessionStorage()) {
    return loadStoredOperationIds(AUTO_DOWNLOADED_OPERATION_IDS_KEY, storage);
}

export function loadDismissedOperationIds(storage = getSessionStorage()) {
    return loadStoredOperationIds(DISMISSED_OPERATION_IDS_KEY, storage);
}

function loadStoredOperationIds(key, storage = getSessionStorage()) {
    if (!storage) {
        return [];
    }

    try {
        const raw = storage.getItem(key);

        if (!raw) {
            return [];
        }

        const parsed = JSON.parse(raw);

        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed.map(operationIdKey).filter(Boolean);
    } catch {
        return [];
    }
}

export function rememberAutoDownloadedOperationId(
    id,
    ids = [],
    storage = getSessionStorage(),
) {
    const next = rememberOperationId(id, ids);

    persistOperationIds(AUTO_DOWNLOADED_OPERATION_IDS_KEY, next, storage);

    return next;
}

export function rememberDismissedOperationId(
    id,
    ids = [],
    storage = getSessionStorage(),
) {
    const next = rememberOperationId(id, ids);

    persistOperationIds(DISMISSED_OPERATION_IDS_KEY, next, storage);

    return next;
}

function rememberOperationId(id, ids = []) {
    const key = operationIdKey(id);

    if (!key) {
        return [...ids];
    }

    return [
        ...ids.map(operationIdKey).filter((stored) => stored && stored !== key),
        key,
    ].slice(-MAX_STORED_IDS);
}

export function persistAutoDownloadedOperationIds(
    ids,
    storage = getSessionStorage(),
) {
    persistOperationIds(AUTO_DOWNLOADED_OPERATION_IDS_KEY, ids, storage);
}

export function persistDismissedOperationIds(
    ids,
    storage = getSessionStorage(),
) {
    persistOperationIds(DISMISSED_OPERATION_IDS_KEY, ids, storage);
}

function persistOperationIds(key, ids, storage = getSessionStorage()) {
    if (!storage) {
        return;
    }

    try {
        storage.setItem(
            key,
            JSON.stringify(ids.map(operationIdKey).filter(Boolean)),
        );
    } catch {
        // Ignore quota / private-mode failures; in-memory ids still apply.
    }
}

function getSessionStorage() {
    try {
        if (typeof sessionStorage === "undefined") {
            return null;
        }

        return sessionStorage;
    } catch {
        return null;
    }
}
